import { useState, useRef, useEffect } from 'react';
import { descriptionGeneratorAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Upload, Loader2, Copy, Sparkles, RefreshCw, X, FileImage, Check, Link as LinkIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const EXAMPLE = `6.90CT Oval Diamond Multi-Shape Band Ring - 14K Yellow Gold
* Carat Weight Total: 6.90CT
* Metal: 14K Yellow Gold
* Average Width: 3.1MM
* Ring Style: Multi-Shape Diamond Band, Prong Set
* Center Stone: 2.50CT
* Center Stone Shape: Oval
* Center Stone Color: E/F
* Center Stone Clarity: VVS2
* Center Stone Cut: Excellent
* Diamond Certification: IGI Certified
* Setting Stone Carat Weight: 4.40CT
* Setting Stone Shape: Pear & Round
* Setting Stone Color: E/F
* Setting Stone Clarity: VS1`;

export default function DescriptionGenerator() {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [urlValue, setUrlValue] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null); // {title, description, full_output}
  const [copied, setCopied] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, WEBP, etc.)');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('Image too large (max 20 MB)');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  };

  const clearAll = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    if (mode === 'upload' && !file) {
      toast.error('Upload a CAD image first');
      return;
    }
    if (mode === 'url' && !urlValue.trim()) {
      toast.error('Paste a URL first');
      return;
    }
    try {
      setAnalyzing(true);
      const res = mode === 'upload'
        ? await descriptionGeneratorAPI.analyzeCad(file)
        : await descriptionGeneratorAPI.analyzeUrl(urlValue.trim());
      setResult(res.data);
      toast.success('Description generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCopy = () => {
    if (!result?.full_output) return;
    navigator.clipboard.writeText(result.full_output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success('Copied');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  return (
    <div className="space-y-6" data-testid="description-generator-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-50 via-white to-zinc-50 border border-violet-100 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-violet-600 rounded-lg">
            <Sparkles size={16} className="text-white" />
          </div>
          <span className="text-xs uppercase tracking-widest text-violet-700 font-medium">
            AI Tool
          </span>
        </div>
        <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight">Description Generator</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Drop any CAD render (PNG, JPG, WEBP — even phone screenshots). Claude Sonnet 4.5 vision reads the jewelry design and outputs a structured product title + full spec sheet in your standard format.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload / Preview */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-900">Source</h3>
            {(file || urlValue) && (
              <button
                onClick={() => {
                  clearAll();
                  setUrlValue('');
                }}
                className="text-xs text-zinc-500 hover:text-red-600 inline-flex items-center gap-1"
                data-testid="clear-cad-btn"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg">
            <button
              onClick={() => { setMode('upload'); setResult(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                mode === 'upload' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
              }`}
              data-testid="mode-upload"
            >
              <Upload size={12} /> Upload image
            </button>
            <button
              onClick={() => { setMode('url'); setResult(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                mode === 'url' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
              }`}
              data-testid="mode-url"
            >
              <LinkIcon size={12} /> From URL
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="cad-file-input"
          />

          {mode === 'url' ? (
            <div className="space-y-3">
              <div>
                <Input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  placeholder="https://example.com/product/... or direct image URL"
                  className="h-10 text-sm"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !analyzing) handleGenerate(); }}
                  data-testid="url-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Paste a product page (Etsy, Amazon, Blue Nile, etc.) or a direct CAD image URL.
                  AI reads the page text + og:image and extracts the spec sheet.
                </p>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={analyzing || !urlValue.trim()}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="generate-btn"
              >
                {analyzing ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyzing URL…</>
                ) : result ? (
                  <><RefreshCw size={14} className="mr-1.5" /> Regenerate</>
                ) : (
                  <><Sparkles size={14} className="mr-1.5" /> Generate from URL</>
                )}
              </Button>
            </div>
          ) : previewUrl ? (
            <div className="space-y-3">
              <div className="relative aspect-square bg-zinc-50 rounded-lg overflow-hidden border border-zinc-200">
                <img
                  src={previewUrl}
                  alt="CAD preview"
                  className="w-full h-full object-contain"
                  data-testid="cad-preview"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-500">
                <span className="truncate pr-2">{file?.name}</span>
                <span className="shrink-0">{(file?.size / 1024).toFixed(0)} KB</span>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={analyzing}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="generate-btn"
              >
                {analyzing ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyzing with AI…</>
                ) : result ? (
                  <><RefreshCw size={14} className="mr-1.5" /> Regenerate</>
                ) : (
                  <><Sparkles size={14} className="mr-1.5" /> Generate description</>
                )}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              className={`w-full aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition-colors cursor-pointer ${
                dragging ? 'border-violet-400 bg-violet-50' : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300 hover:bg-zinc-100'
              }`}
              data-testid="upload-dropzone"
            >
              <div className="p-3 bg-white rounded-full shadow-sm">
                <FileImage size={20} className="text-zinc-500" />
              </div>
              <p className="text-sm font-medium text-zinc-700">Drop CAD image here</p>
              <p className="text-xs text-zinc-500">or click to upload (PNG, JPG, WEBP · max 20 MB)</p>
              <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-zinc-900 text-white text-xs rounded-full">
                <Upload size={11} /> Select image
              </div>
            </button>
          )}
        </div>

        {/* Result */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-900">Generated Description</h3>
            {result?.full_output && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-zinc-200"
                onClick={handleCopy}
                data-testid="copy-description-btn"
              >
                {copied ? <><Check size={12} className="mr-1 text-emerald-600" /> Copied</>
                        : <><Copy size={12} className="mr-1" /> Copy all</>}
              </Button>
            )}
          </div>

          {analyzing ? (
            <div className="aspect-square bg-gradient-to-br from-zinc-50 to-violet-50/30 rounded-lg flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              <p className="text-xs text-zinc-600">Claude is reading the CAD…</p>
              <p className="text-[11px] text-zinc-400">Usually takes 15–30 seconds</p>
            </div>
          ) : result?.full_output ? (
            <div className="space-y-3">
              <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-4" data-testid="result-block">
                <p className="font-semibold text-zinc-900 mb-3 leading-snug" data-testid="result-title">
                  {result.title}
                </p>
                <div className="text-xs text-zinc-700 font-mono whitespace-pre-wrap leading-relaxed" data-testid="result-description">
                  {result.description}
                </div>
              </div>
              <div className="text-[11px] text-zinc-400 flex items-center gap-1">
                <Sparkles size={10} /> Tip: click "Regenerate" on the left if the output needs a different read.
              </div>
            </div>
          ) : (
            <div className="aspect-square bg-zinc-50 border border-dashed border-zinc-200 rounded-lg flex flex-col items-center justify-center gap-3 p-6">
              <p className="text-sm text-zinc-500 font-medium">Preview format</p>
              <div className="text-[11px] text-zinc-500 font-mono whitespace-pre-wrap text-left bg-white border border-zinc-100 rounded p-3 max-h-64 overflow-auto">
                {EXAMPLE}
              </div>
              <p className="text-[11px] text-zinc-400 text-center">
                Upload a CAD and click Generate to see your version.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
